import * as Yup from "yup";
import {
  FILE_VALIDATION,
  VALIDATION_PATTERNS,
  GENDER_OPTIONS,
  RIGHT_TO_WORK_STATUS_BRITISH_IRISH,
  RIGHT_TO_WORK_STATUS_OPTIONS_ALL,
  EMERGENCY_RELATIONSHIP_OPTIONS,
  PREFERRED_WORK_RADIUS_OPTIONS,
  WHAT_MATTERS_MOST_OPTIONS,
  PROFILE_AVAILABILITY_STATUS,
  STARTER_CHECKLIST_OPTIONS,
  STUDENT_LOAN_PLAN_OPTIONS,
  UK_COUNTRY,
  normalizeWhatMattersMost
} from "../../../constants/workerOnboarding";

const isUnder = (file, mb) =>
  file && file.size <= mb * FILE_VALIDATION.BYTES_IN_MB;

export function hasAnyDrivingDocValue(rightToWork = {}) {
  const dvlaShareCode = String(rightToWork?.dvlaShareCode || "").trim();
  const drivingLicenseExpiryDate = String(
    rightToWork?.drivingLicenseExpiryDate || ""
  ).trim();
  const licenseNumber = String(rightToWork?.licenseNumber || "").trim();
  const hasDrivingLicenseFile = rightToWork?.drivingLicense instanceof File;
  const hasDrivingLicenseBackFile =
    rightToWork?.drivingLicenseBack instanceof File;
  const hasExistingDrivingLicense = !!String(
    rightToWork?.drivingLicenseUrl || ""
  ).trim();
  const hasExistingDrivingLicenseBack = !!String(
    rightToWork?.drivingLicenseBackUrl || ""
  ).trim();

  return Boolean(
    hasDrivingLicenseFile ||
      hasDrivingLicenseBackFile ||
      hasExistingDrivingLicense ||
      hasExistingDrivingLicenseBack ||
      dvlaShareCode ||
      drivingLicenseExpiryDate ||
      licenseNumber
  );
}

export function buildProfileAvailabilityValidationSchema(t) {
  return Yup.object({
    status: Yup.string()
      .oneOf(
        [
          PROFILE_AVAILABILITY_STATUS.AVAILABLE,
          PROFILE_AVAILABILITY_STATUS.UNAVAILABLE,
        ],
        t("required")
      )
      .required(t("required")),
    unavailableTill: Yup.string().when("status", {
      is: PROFILE_AVAILABILITY_STATUS.UNAVAILABLE,
      then: (schema) => schema.notRequired().nullable(),
      otherwise: (schema) => schema.notRequired().nullable(),
    }),
  });
}

export const buildWorkerProfileValidationSchema = (t) =>
  Yup.object({
    role: Yup.string().oneOf(["worker"]).required(),

    personalInformation: Yup.object({
      firstName: Yup.string().required(t("required")),
      lastName: Yup.string().required(t("required")),
      dob: Yup.string()
        .required(t("required"))
        .test("age", t("ageMustBe18") || "Age must be at least 18 years", function (value) {
          if (!value) return true;
          const birthDate = new Date(value);
          const today = new Date();
          let age = today.getFullYear() - birthDate.getFullYear();
          const monthDiff = today.getMonth() - birthDate.getMonth();
          if (monthDiff < 0 || (monthDiff === 0 && today.getDate() < birthDate.getDate())) {
            age--;
          }
          return age >= 18;
        }),
      gender: Yup.string()
        .oneOf(Object.values(GENDER_OPTIONS), t("invalidGender"))
        .required(t("required")),
      addressLine1: Yup.string().trim().required(t("required")),
      addressLine2: Yup.string().trim().notRequired(),
      postcode: Yup.string().trim().required(t("required")),
      city: Yup.string().trim().required(t("required")),
      county: Yup.string().trim().notRequired(),
      country: Yup.string()
        .trim()
        .required(t("required"))
        .oneOf(
          [UK_COUNTRY],
          t("locationMustBeUk", "Address must be in the United Kingdom.")
        ),
      languagesSpoken: Yup.array()
        .of(Yup.string().trim())
        .min(1, t("selectAtLeastOne")),
      phoneNumber: Yup.string()
        .required(t("required"))
        .matches(/^[0-9]+$/, t("digitsOnly"))
        .min(10, t("minDigits", { n: 10 }))
        .max(15, t("maxDigits", { n: 15 })),
      email: Yup.string().email(t("emailInvalid")).required(t("required")),
      nationalInsuranceNumber: Yup.string()
        .transform((v) => (v == null ? "" : String(v).trim()))
        .test("ni-optional", t("niNumberInvalid"), (v) => {
          if (!v) return true;
          return VALIDATION_PATTERNS.NATIONAL_INSURANCE.test(String(v).replace(/\s/g, " "));
        }),
      nationality: Yup.string().required(t("required")),
      baseLat: Yup.string().required(t("required")),
      baseLng: Yup.string().required(t("required")),
      jobarkIdSelfie: Yup.mixed()
        .nullable()
        .test("selfie-required", t("uploadSelfieRequired"), function (value) {
          if (value instanceof File) return true;
          const url = this.parent?.uploadSelfieUrl;
          return !!String(url || "").trim();
        })
        .test("f-type", t("fileTypeNotAllowed"), (v) =>
          v == null || !(v instanceof File)
            ? true
            : FILE_VALIDATION.ALLOWED_IMAGE_EXTENSIONS.test(v.name)
        )
        .test(
          "f-size",
          t("fileTooLarge", { n: FILE_VALIDATION.SELFIE_MAX_MB }),
          (v) =>
            v == null || !(v instanceof File)
              ? true
              : isUnder(v, FILE_VALIDATION.SELFIE_MAX_MB)
        )
    }),

    emergencyContact: Yup.object({
      firstName: Yup.string().required(t("required")),
      lastName: Yup.string().required(t("required")),
      relationshipToWorker: Yup.string()
        .oneOf(
          EMERGENCY_RELATIONSHIP_OPTIONS.map((o) => o.value),
          t("required")
        )
        .required(t("required")),
      phoneNumber: Yup.string()
        .required(t("required"))
        .matches(/^[0-9]+$/, t("digitsOnly"))
        .min(10, t("minDigits", { n: 10 }))
        .max(15, t("maxDigits", { n: 15 })),
      alternativePhoneNumber: Yup.string()
        .nullable()
        .notRequired()
        .test("optional-phone", t("digitsOnly"), function (value) {
          if (!value || value.trim() === "") return true;
          const digitsOnly = value.replace(/[^0-9]/g, "");
          return (
            /^[0-9]+$/.test(digitsOnly) &&
            digitsOnly.length >= 10 &&
            digitsOnly.length <= 15
          );
        })
        .test(
          "diff-from-primary",
          t("emergencyAltPhoneMustDiffer"),
          function (value) {
            if (!value || String(value).trim() === "") return true;
            const primary = this.parent?.phoneNumber;
            if (primary == null || String(primary).trim() === "") return true;
            const altDigits = String(value).replace(/[^0-9]/g, "");
            const primaryDigits = String(primary).replace(/[^0-9]/g, "");
            if (!altDigits || !primaryDigits) return true;
            return altDigits !== primaryDigits;
          }
        )
    }),

    rightToWork: Yup.object().shape({
      rightToWorkStatus: Yup.string()
        .required(t("required"))
        .oneOf(RIGHT_TO_WORK_STATUS_OPTIONS_ALL, t("invalidRightToWorkStatus")),
      shareCode: Yup.string().when("rightToWorkStatus", {
        is: (status) => status && status !== RIGHT_TO_WORK_STATUS_BRITISH_IRISH,
        then: (schema) => schema.required(t("shareCodeRequired")),
        otherwise: (schema) => schema.notRequired()
      }),
      visaExpiryDate: Yup.string().when("rightToWorkStatus", {
        is: (status) => status && status !== RIGHT_TO_WORK_STATUS_BRITISH_IRISH,
        then: (schema) => schema.required(t("required")),
        otherwise: (schema) => schema.notRequired()
      }),
      drivingLicense: Yup.mixed()
        .nullable()
        .test("driving-license-type", t("fileTypeNotAllowed"), (v) =>
          v == null
            ? true
            : v instanceof File
            ? FILE_VALIDATION.ALLOWED_EXTENSIONS.test(v.name)
            : false
        )
        .test(
          "driving-license-size",
          t("fileTooLarge", { n: FILE_VALIDATION.MAX_MB }),
          (v) =>
            v == null
              ? true
              : v instanceof File
              ? isUnder(v, FILE_VALIDATION.MAX_MB)
              : false
        )
        .test("driving-license-required", t("required"), function (value) {
          const anyDrivingDoc = hasAnyDrivingDocValue(this.parent || {});
          if (!anyDrivingDoc) return true;
          return value instanceof File || !!this.parent?.drivingLicenseUrl;
        }),
      drivingLicenseBack: Yup.mixed()
        .nullable()
        .test("driving-license-back-type", t("fileTypeNotAllowed"), (v) =>
          v == null
            ? true
            : v instanceof File
            ? FILE_VALIDATION.ALLOWED_EXTENSIONS.test(v.name)
            : false
        )
        .test(
          "driving-license-back-size",
          t("fileTooLarge", { n: FILE_VALIDATION.MAX_MB }),
          (v) =>
            v == null
              ? true
              : v instanceof File
              ? isUnder(v, FILE_VALIDATION.MAX_MB)
              : false
        ),
      drivingLicenseExpiryDate: Yup.string().test(
        "driving-license-expiry-required",
        t("required"),
        function (value) {
          const anyDrivingDoc = hasAnyDrivingDocValue(this.parent || {});
          if (!anyDrivingDoc) return true;
          return !!String(value || "").trim();
        }
      ),
      dvlaShareCode: Yup.string().test(
        "dvla-share-code-required",
        t("required"),
        function (value) {
          const anyDrivingDoc = hasAnyDrivingDocValue(this.parent || {});
          if (!anyDrivingDoc) return true;
          return !!String(value || "").trim();
        }
      ),
      licenseNumber: Yup.string().test(
        "license-number-required",
        t("required"),
        function (value) {
          const anyDrivingDoc = hasAnyDrivingDocValue(this.parent || {});
          if (!anyDrivingDoc) return true;
          return !!String(value || "").trim();
        }
      ),
      uploadId: Yup.array()
        .of(
          Yup.mixed()
            .test("f-type", t("fileTypeNotAllowed"), (v) =>
              v instanceof File ? FILE_VALIDATION.ALLOWED_EXTENSIONS.test(v.name) : false
            )
            .test(
              "f-size",
              t("fileTooLarge", { n: FILE_VALIDATION.MAX_MB }),
              (v) => (v instanceof File ? isUnder(v, FILE_VALIDATION.MAX_MB) : false)
            )
        )
        .max(
          FILE_VALIDATION.MAX_FILES_PER_FIELD,
          t("maxFilesAllowed", { n: FILE_VALIDATION.MAX_FILES_PER_FIELD })
        )
        .test("upload-id-required", t("uploadIdRequired"), function (value) {
          const files = value || [];
          const existing = this.parent?.uploadIdUrls || [];
          if (existing.length > 0) return true;
          return files.length >= 1;
        }),
      proofOfAddress: Yup.array()
        .of(
          Yup.mixed()
            .test("f-type", t("fileTypeNotAllowed"), (v) =>
              v instanceof File ? FILE_VALIDATION.ALLOWED_EXTENSIONS.test(v.name) : false
            )
            .test(
              "f-size",
              t("fileTooLarge", { n: FILE_VALIDATION.MAX_MB }),
              (v) => (v instanceof File ? isUnder(v, FILE_VALIDATION.MAX_MB) : false)
            )
        )
        .max(
          FILE_VALIDATION.MAX_FILES_PER_FIELD,
          t("maxFilesAllowed", { n: FILE_VALIDATION.MAX_FILES_PER_FIELD })
        )
        .test("proof-required", t("proofOfAddressRequired"), function (value) {
          const files = value || [];
          const existing = this.parent?.proofOfAddressUrls || [];
          if (existing.length > 0) return true;
          return files.length >= 1;
        })
    }),

    employmentTax: Yup.object().shape({
      hasP45: Yup.string()
        .oneOf(["yes", "no"], t("required"))
        .required(t("required")),
      p45Upload: Yup.mixed()
        .nullable()
        .when("hasP45", {
          is: "yes",
          then: (schema) =>
            schema.test("p45-required", t("p45UploadRequired"), function (value) {
              if (value instanceof File) return true;
              const existing = this.parent?.p45FileUrls || [];
              return existing.length > 0;
            }),
          otherwise: (schema) => schema.nullable(),
        }),
      starterChecklistChoice: Yup.string().when("hasP45", {
        is: "no",
        then: (schema) =>
          schema
            .oneOf(
              STARTER_CHECKLIST_OPTIONS.map((opt) => opt.value),
              t("required")
            )
            .required(t("required")),
        otherwise: (schema) => schema.notRequired(),
      }),
      hasPostgraduateLoan: Yup.string().when("hasP45", {
        is: "no",
        then: (schema) =>
          schema.oneOf(["yes", "no"], t("required")).required(t("required")),
        otherwise: (schema) => schema.notRequired(),
      }),
      planType: Yup.string().when(["hasP45", "hasPostgraduateLoan"], {
        is: (hasP45, hasPostgraduateLoan) =>
          hasP45 === "no" && hasPostgraduateLoan === "yes",
        then: (schema) =>
          schema
            .oneOf(
              STUDENT_LOAN_PLAN_OPTIONS.map((opt) => opt.value),
              t("required")
            )
            .required(t("required")),
        otherwise: (schema) => schema.notRequired(),
      }),
      notes: Yup.string().notRequired(),
      acceptEmergencyTaxCode: Yup.boolean()
        .oneOf([true], t("required"))
        .required(t("required")),
    }),

    bankDetails: Yup.object({
      accountHolderName: Yup.string().required(t("required")),
      sortCode: Yup.string()
        .matches(VALIDATION_PATTERNS.SORT_CODE, t("sortCodeFormat"))
        .required(t("required")),
      accountNumber: Yup.string()
        .matches(VALIDATION_PATTERNS.ACCOUNT_NUMBER, t("numeric"))
        .min(7, t("minDigits", { n: 7 }))
        .required(t("required")),
      bankName: Yup.string().required(t("required"))
    }),

    moreAboutYou: Yup.object({
      healthAccessibilityDetails: Yup.string()
        .trim()
        .required(t("required"))
        .min(1, t("required")),
      hasNoConvictions: Yup.boolean(),
      hasConvictionsOrPending: Yup.boolean(),
      criminalConvictionsDetails: Yup.string().when(["hasConvictionsOrPending"], {
        is: (hasConvictionsOrPending) => hasConvictionsOrPending === true,
        then: (schema) => schema.trim().required(t("required")),
        otherwise: (schema) => schema.notRequired().nullable()
      }),
      aboutYouWorkHistory: Yup.string().trim().required(t("required"))
    }).test(
      "criminal-convictions-choice",
      t("moreAboutYouConvictionsRequired") ||
        "Please confirm whether you have any unspent convictions or pending cases.",
      (obj) =>
        obj?.hasNoConvictions === true || obj?.hasConvictionsOrPending === true
    ),

    workPreferences: Yup.object({
      sectors: Yup.array()
        .of(
          Yup.object({
            sectorSlug: Yup.string().required(t("required")),
            roles: Yup.array().of(Yup.string().trim().min(1)).min(1, t("required"))
          })
        )
        .min(1, t("addAtLeastOne"))
        .test(
          "unique-sectors",
          t("duplicateSectorNotAllowed") || "Duplicate sector not allowed.",
          (arr) => {
            const slugs = (arr || []).map((s) => s?.sectorSlug).filter(Boolean);
            return slugs.length === new Set(slugs).size;
          }
        ),
      preferredWorkRadius: Yup.number()
        .typeError(t("numeric"))
        .oneOf(PREFERRED_WORK_RADIUS_OPTIONS, t("preferredRadiusAllowed"))
        .required(t("required")),
      availability: Yup.array().of(Yup.string()).min(1, t("selectAtLeastOne")),
      transport: Yup.array().of(Yup.string()).min(1, t("selectAtLeastOne")),
      optOut48HourLimit: Yup.boolean().required(),
      whatMattersMost: Yup.array()
        .transform((val) => normalizeWhatMattersMost(val))
        .of(Yup.string().oneOf(WHAT_MATTERS_MOST_OPTIONS))
        .min(1, t("selectAtLeastOne"))
        .required(t("required"))
    }),

    profileAvailability: Yup.object({
      status: Yup.string()
        .oneOf(
          [PROFILE_AVAILABILITY_STATUS.AVAILABLE, PROFILE_AVAILABILITY_STATUS.UNAVAILABLE],
          t("required")
        )
        .required(t("required")),
      unavailableTill: Yup.string().when("status", {
        is: PROFILE_AVAILABILITY_STATUS.UNAVAILABLE,
        then: (schema) => schema.notRequired().nullable(),
        otherwise: (schema) => schema.notRequired().nullable(),
      }),
    }),

    certificateEntries: Yup.array()
      .of(
        Yup.object().shape({
          id: Yup.number().required(),
          certSlug: Yup.string(),
          file: Yup.mixed().nullable(),
          serviceNumber: Yup.string(),
          existingFiles: Yup.array().nullable()
        })
      )
      .nullable()
  });

export function validateCertificateEntries(values, certsData, t) {
  const entries = values.certificateEntries || [];
  const certErrors = [];
  let hasCertError = false;

  entries.forEach((entry, idx) => {
    if (!entry?.certSlug) {
      certErrors[idx] = null;
      return;
    }
    const cert = certsData.find((c) => c.slug === entry.certSlug);
    if (!cert) {
      certErrors[idx] = null;
      return;
    }
    const hasFile = !!(entry?.file && entry.file instanceof File);
    const hasExisting =
      Array.isArray(entry?.existingFiles) && entry.existingFiles.length > 0;
    const hasServiceNumber = !!entry?.serviceNumber?.trim?.();

    if (cert.hasServiceNumber) {
      if (!hasFile && !hasServiceNumber && !hasExisting) {
        certErrors[idx] = {
          message:
            t("certFileOrServiceNumberRequired") ||
            "Please upload a certificate file and/or enter the DBS service number."
        };
        hasCertError = true;
      } else {
        certErrors[idx] = null;
      }
      return;
    }
    if (!hasFile && !hasExisting) {
      certErrors[idx] = {
        message:
          t("selectFileForCertificate") ||
          "Please select a file for this certificate type."
      };
      hasCertError = true;
    } else {
      certErrors[idx] = null;
    }
  });

  return hasCertError ? certErrors : null;
}
